import { Button, Dialog } from '@mui/material';
import {
  bookmark_gray400,
  bookmark_white_full,
  check_gray400,
  check_white,
  chevron_down_gray300,
  chevron_down_gray400,
  chevron_up_gray300,
  chevron_up_gray400,
  download_icon_white,
  sequence_icon,
  x_black
} from '@assets/icons';
import { colorSet } from '@theme';
import { IImageListItem, IImageModalType, ISelectedImageInfo, ISequenceCutListItem, ISequenceInfo } from '@types';
import { useFetchImageDetail, useFetchSequenceCutList, useFetchSimilarImageList } from '@api';
import { useEffect, useRef, useState } from 'react';
import { Text } from '../common/Text';
import { SecureImage } from './SecureImage';
import { useAdultsOnlyActions, useComicActions } from '@remote-hooks';
import {
  OutputSortTypeEnum,
  FemaleBodyChildOptionValueEnum,
  PathnameEnum,
  SexualPositionChildOptionValueEnum,
  ToastMessageEnum,
  StandardFrameChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum,
  AgeRangeChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  SexualBodyPartChildOptionValueEnum,
  SelectedComicOptionEnum
} from '@constant';
import { downloadImage, getEnumKeyByValue } from '@utils';
import { CollectionSelectModal, CollectionUpsertModal } from '@components';
import { useCollection, useToast } from '@hooks';
import { useLocation, useNavigate } from 'react-router';
import { ComicNameSearchIcon } from '@assets/iconComponents';

interface IImageModal {
  isVisible: boolean;
  onClose: () => void;
  initialImageModalType: IImageModalType;
  initialSelectedImageInfo: ISelectedImageInfo | null;
  initialSequenceInfo: ISequenceInfo | null;
  onUseButtonClick?: (imageId: number, isUsed: boolean, usedCount: number, isCollected?: boolean) => void;
  createQueryStringForImageList?: () => string;
}

export const ImageModal = (props: IImageModal) => {
  const {
    isVisible,
    onClose,
    initialImageModalType,
    initialSelectedImageInfo,
    initialSequenceInfo,
    onUseButtonClick,
    createQueryStringForImageList
  } = props;

  const {
    isCollectionSelectModalVisible,
    handleCollectionSelectModalOpen,
    handleCollectionSelectModalClose,
    isCollectionUpsertModalVisible,
    showGoBackButton,
    handleCollectionUpsertModalOpen,
    handleCollectionUpsertModalClose,
    handleGoBackToCollectionSelectModal
  } = useCollection();

  const { showToast } = useToast();

  const [imageModalType, setImageModalType] = useState<IImageModalType>(initialImageModalType);
  const [selectedImageInfo, setSelectedImageInfo] = useState<ISelectedImageInfo | null>(initialSelectedImageInfo);
  const [sequenceInfo, setSequenceInfo] = useState<ISequenceInfo | null>(initialSequenceInfo);

  //시퀀스 컷 관련
  const [sequenceCutList, setSequenceCutList] = useState<ISequenceCutListItem[]>([]);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isSingleCutModal = !imageModalType.needSequence && !imageModalType.needSimilar;
  const visibleCount = 4; // 화면에 보여지는 시퀀스 컷 리스트 아이템 개수

  const navigate = useNavigate();
  const location = useLocation();
  const locationPathname = location.pathname;
  const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE;
  const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE; // 19금 Output 페이지인 경우 true

  const { onSetAdultsOnly } = useAdultsOnlyActions();
  const { onSetComic } = useComicActions();

  const { data: sequenceCutListData } = useFetchSequenceCutList(
    imageModalType.needSequence ? sequenceInfo?.id : undefined
  );

  const { data: similarImageListData } = useFetchSimilarImageList(
    imageModalType.needSimilar ? selectedImageInfo?.id : undefined
  );

  const { data: imageDetailData } = useFetchImageDetail(imageModalType.needSimilar ? selectedImageInfo?.id : undefined);

  // 모달 타이틀 텍스트값 결정 및 각각의 필터값 렌더링 여부 결정
  const isStandardImage = imageDetailData?.imageOptionValue['연령대'];
  const isAdultsOnlyImage = imageDetailData?.imageOptionValue['체위'];

  const handleComicNameSearchClick = () => {
    if (imageDetailData?.comic?.name) {
      if (isAdultsOnlyOutputPage) {
        //19금 Output 페이지에 있을 시
        onSetComic({
          selectedOption: SelectedComicOptionEnum.COMIC_NAME,
          genreList: [],
          comicNameList: [imageDetailData.comic.name]
        });
        onClose();
      } else {
        //19금 Output 이외의 페이지에 있을 시
        onSetComic({
          selectedOption: SelectedComicOptionEnum.COMIC_NAME,
          genreList: [],
          comicNameList: [imageDetailData.comic.name]
        });
        //19금 Output 페이지로 이동
        navigate(PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE);
      }
    }
  };

  const handleSequenceCutListItemClick = (id: number, s3Url: string) => {
    setSelectedImageInfo({ id: id, s3Url: s3Url });

    const updatedImageList = sequenceCutList.map((item) => ({
      ...item, // 기존의 속성들을 그대로 복사
      isFocused: item.id === id // image id가 일치하는 경우에만 isFocused를 true로 설정, 나머지는 false
    }));
    setSequenceCutList([...updatedImageList]);
  };

  const handleSequenceCutListUpButtonClick = () => {
    if (visibleStartIndex > 0) {
      setVisibleStartIndex((prevIndex) => prevIndex - 1);
    }
  };

  const handleSequenceCutListDownButtonClick = () => {
    if (visibleStartIndex + visibleCount < sequenceCutList.length) {
      setVisibleStartIndex((prevIndex) => prevIndex + 1);
    }
  };

  // ImageModal 내 북마크 버튼 클릭 -> 컬렉션 선택 모달에서 PUT images/:id/collections 요청 성공 -> 북마크 버튼 업데이트를 위한 함수
  const handleImageCollected = (isCollected: boolean) => {
    if (selectedImageInfo) {
      setSelectedImageInfo({
        ...selectedImageInfo,
        isCollected
      });
    }
  };

  const handleTagClick = (tag: string) => {
    if (isStandardOutputPage) {
      return;
    } else if (isAdultsOnlyOutputPage) {
      //19금 Output 페이지에 있을 시 기존 옵션값을 유지
      onSetAdultsOnly({ selectedMetaTags: [tag] });
      onClose();
    } else {
      //19금 Output 이외의 페이지에 있을 시 옵션값을 initialize
      onSetAdultsOnly({
        isIncludeUsed: true,
        isIncludeOtherUsersUsed: true,
        sortType: OutputSortTypeEnum.RANDOM,
        sexualPosition: SexualPositionChildOptionValueEnum['정상위'],
        characterNumber: null,
        femaleBody: null,
        sexualBodyPart: null,
        frame: null,
        angleFirst: null,
        angleSecond: null,
        selectedMetaTags: [tag]
      });
      // 19금 Output 이외의 페이지에 있었을 시 19금 Output 페이지로 이동
      navigate(PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE);
    }
  };

  const handleSimilarImageClick = (imageId: number, s3Url: string, sequenceInfo: ISequenceInfo | null) => {
    setImageModalType({
      needSequence: Boolean(sequenceInfo),
      needSimilar: isStandardOutputPage || isAdultsOnlyOutputPage
    });
    setSelectedImageInfo({ id: imageId, s3Url: s3Url });
    setSequenceInfo(sequenceInfo);

    const scrollBody = document.querySelector('.MuiDialog-scrollBody');
    if (scrollBody) {
      scrollBody.scrollTop = 0;
    }
  };

  useEffect(() => {
    setImageModalType(initialImageModalType);
    setSelectedImageInfo(initialSelectedImageInfo);
    setSequenceInfo(initialSequenceInfo);
  }, [initialImageModalType, initialSelectedImageInfo, initialSequenceInfo]);

  //시퀀스 이미지 리스트에서 선택된 컷을 찾아 isFocused: true 처리
  useEffect(() => {
    if (isVisible && sequenceCutListData && sequenceCutListData.length > 0) {
      const selectedImageIndex = sequenceInfo?.order ? sequenceInfo.order - 1 : 0;

      //imageList state에 isFocused 프로퍼티 추가
      const updatedImageList = sequenceCutListData.map((item, index) => ({
        ...item, // 기존의 속성들을 그대로 복사
        isFocused: index === selectedImageIndex // index가 order-1와 일치하는 경우에만 isFocused를 true로 설정, 나머지는 false
      }));
      setSequenceCutList([...updatedImageList]);

      //5번째 이후 이미지라면 imageList의 최대한 앞 순서에 보여주기
      if (selectedImageIndex >= visibleCount) {
        setVisibleStartIndex(Math.min(selectedImageIndex, updatedImageList.length - visibleCount));
      } else {
        setVisibleStartIndex(0);
      }
    } else {
      setSequenceCutList([]);
    }
  }, [isVisible, sequenceCutListData]);

  return (
    <>
      <Dialog
        open={isVisible}
        onClose={onClose}
        scroll="body"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)'
          },
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'calc(100% - 288px)',
            //19금 이외 단일컷 모달인 경우 fix된 height를 가지며 스크롤 불가
            height: isSingleCutModal ? 'calc(100% - 80px)' : 'auto',
            maxHeight: isSingleCutModal ? 'calc(100% - 80px)' : 'auto',
            margin: '40px 144px',
            padding: '60px 40px',
            boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.06)',
            borderRadius: 0
          }
        }}
      >
        <div ref={scrollRef}>
          <img
            src={x_black}
            alt=""
            style={{ width: 24, height: 24, position: 'absolute', top: 24, right: 24, cursor: 'pointer' }}
            onClick={onClose}
          />
        </div>

        {/* 19금 이미지 식별 정보 */}
        {imageModalType.needSimilar ? (
          <div style={{ minHeight: 58, marginBottom: '32px' }}>
            {isStandardImage ? (
              <Text variant="h3" fontWeight="bold" marginBottom="10px">
                스탠다드 검색
              </Text>
            ) : isAdultsOnlyImage ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: '10px',
                  gap: 10
                }}
              >
                <Text variant="h3" color={colorSet.gray500} fontWeight="bold">
                  [19금] {imageDetailData?.comic?.name ?? ''}
                </Text>
                {imageDetailData?.comic?.name ? <ComicNameSearchIcon onClick={handleComicNameSearchClick} /> : <div />}
              </div>
            ) : (
              <div style={{ height: 28 }} />
            )}

            {imageDetailData && imageDetailData.imageOptionValue ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: isStandardImage ? '10px 40px' : '10px 60px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {isStandardImage && imageDetailData?.imageOptionValue['배경컷'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    배경컷
                  </Text>
                ) : null}

                {isAdultsOnlyImage && imageDetailData?.comic?.genre ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    장르: {imageDetailData?.comic?.genre}
                  </Text>
                ) : null}

                {isAdultsOnlyImage && imageDetailData?.imageOptionValue['체위'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    체위:{' '}
                    {(() => {
                      const sexualPositionValue = imageDetailData?.imageOptionValue['체위'];
                      return getEnumKeyByValue(SexualPositionChildOptionValueEnum, sexualPositionValue);
                    })()}
                  </Text>
                ) : null}

                {isAdultsOnlyImage && imageDetailData?.imageOptionValue['여체'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    여체:{' '}
                    {(() => {
                      const femaleBodyValue = imageDetailData?.imageOptionValue['여체'];
                      return getEnumKeyByValue(FemaleBodyChildOptionValueEnum, femaleBodyValue);
                    })()}
                  </Text>
                ) : null}

                {isAdultsOnlyImage &&
                imageDetailData?.imageOptionValue['신체부위'] &&
                imageDetailData?.imageOptionValue['신체부위'].length > 0 ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    신체부위:{' '}
                    {(() => {
                      const bodyPartValue = imageDetailData?.imageOptionValue['신체부위'];
                      return getEnumKeyByValue(SexualBodyPartChildOptionValueEnum, bodyPartValue) ?? '';
                    })()}
                  </Text>
                ) : null}

                {imageDetailData?.imageOptionValue['남성 인원수'] ||
                imageDetailData?.imageOptionValue['여성 인원수'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    인원수:{' '}
                    {imageDetailData?.imageOptionValue['남성 인원수'] &&
                    imageDetailData?.imageOptionValue['남성 인원수'] > 0
                      ? `남자 ${imageDetailData?.imageOptionValue['남성 인원수']}명`
                      : ''}{' '}
                    {imageDetailData?.imageOptionValue['여성 인원수'] &&
                    imageDetailData?.imageOptionValue['여성 인원수'] > 0
                      ? `여자 ${imageDetailData?.imageOptionValue['여성 인원수']}명`
                      : ''}
                  </Text>
                ) : null}

                {imageDetailData?.imageOptionValue['샷 구분'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    프레임:{' '}
                    {(() => {
                      const frameValue = imageDetailData?.imageOptionValue['샷 구분'];
                      return getEnumKeyByValue(StandardFrameChildOptionValueEnum, frameValue);
                    })()}
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['연출'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    연출
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    세로앵글:{' '}
                    {(() => {
                      const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                      return getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue);
                    })()}
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['연령대'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    연령대:{' '}
                    {(() => {
                      const ageRangeValue = imageDetailData?.imageOptionValue['연령대'];
                      return getEnumKeyByValue(AgeRangeChildOptionValueEnum, ageRangeValue);
                    })()}
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['중심동작부위'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    중심 동작 부위:{' '}
                    {(() => {
                      const mainMotionPartValue = imageDetailData?.imageOptionValue['중심동작부위'];
                      return getEnumKeyByValue(MainMotionPartChildOptionValueEnum, mainMotionPartValue);
                    })()}
                  </Text>
                ) : null}

                {isStandardImage &&
                imageDetailData?.imageOptionValue['표정'] &&
                imageDetailData?.imageOptionValue['표정'].length > 0 ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    표정:{' '}
                    {(() => {
                      const facialExpressionValue = imageDetailData?.imageOptionValue['표정'];
                      return (
                        getEnumKeyByValue(StandardFacialExpressionChildOptionValueEnum, facialExpressionValue) ?? ''
                      );
                    })()}
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['데포르메 여부'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    데포르메
                  </Text>
                ) : null}

                {imageDetailData?.imageOptionValue['가로앵글'] &&
                imageDetailData?.imageOptionValue['가로앵글'].length > 0 ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    가로앵글:{' '}
                    {(() => {
                      const horizontalAngleValue = imageDetailData?.imageOptionValue['가로앵글'];
                      return (
                        getEnumKeyByValue(HorizontalAngleChildOptionValueEnum, horizontalAngleValue) ??
                        horizontalAngleValue
                      );
                    })()}
                  </Text>
                ) : null}

                {isStandardImage &&
                imageDetailData?.imageOptionValue['동작구분'] &&
                imageDetailData?.imageOptionValue['동작구분'].length > 0 ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    동작구분:{' '}
                    {(() => {
                      const postureCategoryValue = imageDetailData?.imageOptionValue['동작구분'];
                      return (
                        getEnumKeyByValue(PostureCategoryChildOptionValueEnum, postureCategoryValue) ??
                        postureCategoryValue
                      );
                    })()}
                  </Text>
                ) : null}

                {isStandardImage && imageDetailData?.imageOptionValue['스킨십'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    스킨십
                  </Text>
                ) : null}

                {isAdultsOnlyImage && imageDetailData?.imageOptionValue['세로앵글'] ? (
                  <Text variant="body1" color={colorSet.gray500}>
                    세로앵글:{' '}
                    {(() => {
                      const verticalAngleValue = imageDetailData?.imageOptionValue['세로앵글'];
                      return (
                        getEnumKeyByValue(VerticalAngleChildOptionValueEnum, verticalAngleValue) ?? verticalAngleValue
                      );
                    })()}
                  </Text>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* 선택된 컷 및 시퀀스 컷 리스트 */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            height: imageModalType.needSimilar ? 'calc(100vh - 124px)' : 'calc(100vh - 200px)' //scroll=paper로 변경 필요할 시 해당 줄 지우고 상단 & .MuiDialog-paper 블록 내 두 줄 활성화
          }}
        >
          {/* 선택된 컷 */}
          <div
            style={{
              width: '100%',
              height: imageModalType.needSimilar ? 'calc(100% - 76px)' : '100%',
              borderRadius: '6px',
              border: `1px solid ${colorSet.gray300}`,
              backgroundColor: colorSet.gray100
            }}
          >
            <SecureImage
              imageUrl={selectedImageInfo?.s3Url ?? ''}
              imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />

            {/* 사용 / 북마크 / 다운로드 버튼 */}
            {imageModalType.needSimilar ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  marginTop: '32px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'row', gap: 12 }}>
                  <Button
                    onClick={() => {
                      if (onUseButtonClick) {
                        onUseButtonClick(
                          selectedImageInfo?.id ?? 0,
                          selectedImageInfo?.isUsed ?? false,
                          selectedImageInfo?.usedCount ?? 0,
                          selectedImageInfo?.isCollected
                        );
                      }
                    }}
                    size="medium"
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 10,
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: selectedImageInfo?.isUsed ? 'none' : `1px solid ${colorSet.gray400}`,
                      backgroundColor: selectedImageInfo?.isUsed ? colorSet.gray600 : colorSet.white
                    }}
                  >
                    <img
                      src={selectedImageInfo?.isUsed ? check_white : check_gray400}
                      alt=""
                      style={{ width: 20, height: 20 }}
                    />
                    <Text variant="body1" color={selectedImageInfo?.isUsed ? colorSet.white : colorSet.gray600}>
                      {selectedImageInfo?.usedCount ? selectedImageInfo?.usedCount.toLocaleString() : 0}
                    </Text>
                  </Button>
                  <Button
                    onClick={() => {
                      handleCollectionSelectModalOpen();
                    }}
                    style={{
                      width: '84px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: selectedImageInfo?.isCollected ? 'none' : `1px solid ${colorSet.gray400}`,
                      backgroundColor: selectedImageInfo?.isCollected ? colorSet.gray600 : colorSet.white
                    }}
                  >
                    <img
                      src={selectedImageInfo?.isCollected ? bookmark_white_full : bookmark_gray400}
                      alt=""
                      style={{ width: 20, height: 20 }}
                    />
                  </Button>
                </div>
                <Button
                  onClick={() => {
                    downloadImage(selectedImageInfo?.s3Url).then((isSuccess) => {
                      if (isSuccess) {
                        showToast(ToastMessageEnum.IMAGE_DOWNLOAD_SUCCESS);
                      }
                    });
                  }}
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 10,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <img src={download_icon_white} alt="" style={{ width: 20, height: 20 }} />
                  다운로드
                </Button>
              </div>
            ) : null}
          </div>

          {/* 시퀀스 컷 리스트 */}
          {imageModalType.needSequence ? (
            <div
              style={
                {
                  height: imageModalType.needSimilar ? 'calc(100% - 76px)' : '100%',
                  marginLeft: '40px',
                  '--sequence-cut-list-item-height': 'calc((100% - 152px) / 4)'
                } as React.CSSProperties
              }
            >
              <Text variant="subtitle" fontWeight="bold" marginBottom="8px">
                시퀀스 컷
              </Text>

              <Button
                onClick={handleSequenceCutListUpButtonClick}
                disabled={visibleStartIndex === 0}
                sx={{
                  width: '100%',
                  height: 32,
                  marginBottom: '16px',
                  borderRadius: '6px',
                  border: `1px solid ${colorSet.gray300}`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  // disabled 상태일 때 스타일
                  '&.Mui-disabled': {
                    backgroundColor: colorSet.gray100,
                    border: 'none',
                    cursor: 'not-allowed'
                  }
                }}
              >
                <img
                  src={visibleStartIndex === 0 ? chevron_up_gray300 : chevron_up_gray400}
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </Button>

              {sequenceCutList.length > 0 ? (
                sequenceCutList.slice(visibleStartIndex, visibleStartIndex + visibleCount).map((item, index) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      handleSequenceCutListItemClick(item.id, item.s3Url);
                    }}
                    style={{
                      position: 'relative', // 자식 요소들이 절대 위치를 가질 수 있도록
                      height: 'var(--sequence-cut-list-item-height)',
                      aspectRatio: 1,
                      cursor: 'pointer',
                      marginBottom: index === 3 ? 0 : 8
                    }}
                  >
                    <SecureImage
                      imageUrl={item.s3Url}
                      imageStyle={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: item.isFocused ? `4px solid ${colorSet.primaryMain}` : `1px solid ${colorSet.gray300}`
                      }}
                    />
                    {item.isFocused ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(115, 103, 240, 0.4)',
                          borderRadius: '6px' // 부모 div와 동일한 border-radius 적용
                        }}
                      />
                    ) : null}
                  </div>
                ))
              ) : (
                // imageList의 length가 0일 시에도 우측 레이아웃 width 유지
                <div style={{ height: 'var(--sequence-cut-list-item-height)', aspectRatio: 1 }} />
              )}

              <Button
                onClick={handleSequenceCutListDownButtonClick}
                disabled={visibleStartIndex + visibleCount >= sequenceCutList.length}
                sx={{
                  width: '100%',
                  height: 32,
                  marginTop: '16px',
                  borderRadius: '6px',
                  border: `1px solid ${colorSet.gray300}`,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  // disabled 상태일 때 스타일
                  '&.Mui-disabled': {
                    backgroundColor: colorSet.gray100,
                    border: 'none',
                    cursor: 'not-allowed'
                  }
                }}
              >
                <img
                  src={
                    visibleStartIndex + visibleCount >= sequenceCutList.length
                      ? chevron_down_gray300
                      : chevron_down_gray400
                  }
                  alt=""
                  style={{ width: 24, height: 24 }}
                />
              </Button>
            </div>
          ) : null}
        </div>

        {imageModalType.needSimilar &&
        imageDetailData &&
        imageDetailData.metaTag &&
        imageDetailData.metaTag.length > 0 ? (
          <div style={{ marginTop: '32px' }}>
            <Text variant="h3" fontWeight="bold" marginBottom="10px">
              관련 태그
            </Text>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: `10px`,
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              {imageDetailData.metaTag.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    handleTagClick(item.name);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '6px',
                    backgroundColor: colorSet.gray200,
                    padding: '8px 20px',
                    cursor: isStandardOutputPage ? 'default' : 'pointer'
                  }}
                >
                  <Text variant="body1">{item.name}</Text>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {imageModalType.needSimilar ? (
          <div style={{ margin: '80px 0px', borderBottom: `1px solid ${colorSet.gray300}` }} />
        ) : null}

        {/* 유사 이미지 더보기 */}
        {imageModalType.needSimilar ? (
          <div>
            <Text variant="h2" fontWeight="bold" marginBottom="40px">
              유사 이미지 더보기
            </Text>

            {/* 유사 이미지 리스트 */}
            <div
              style={
                {
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '15px 10px',
                  '--similar-image-list-item-width': 'calc((100% - 50px) / 6)'
                } as React.CSSProperties
              }
            >
              {similarImageListData && similarImageListData.length > 0 ? (
                similarImageListData.map((item: IImageListItem) => {
                  return (
                    <div
                      key={item.id}
                      style={{
                        position: 'relative',
                        width: 'var(--similar-image-list-item-width)', // 7개 아이템에 맞는 너비 계산
                        aspectRatio: '1', // 비율을 1:1로 설정하여 정사각형 형태 유지
                        overflow: 'hidden', // 이미지가 부모 크기를 넘지 않도록 설정
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        handleSimilarImageClick(
                          item.id,
                          item.s3Url,
                          item.sequence && item.sequenceOrder
                            ? { id: item.sequence.id, order: item.sequenceOrder }
                            : null
                        );
                      }}
                    >
                      <SecureImage
                        imageUrl={item.s3ThumbnailUrl ?? item.s3Url}
                        imageStyle={{
                          width: '100%', // 부모 div의 너비를 채우도록 설정
                          height: '100%', // 부모 div의 높이를 채우도록 설정
                          objectFit: 'cover', // 이미지 비율을 유지하면서 채우기
                          borderRadius: '6px',
                          border: `1px solid ${colorSet.gray300}`
                        }}
                      />
                      {item.sequence ? (
                        <img
                          src={sequence_icon}
                          alt=""
                          style={{
                            width: 44,
                            height: 44,
                            position: 'absolute',
                            top: 4,
                            right: 0
                          }}
                        />
                      ) : null}
                    </div>
                  );
                })
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: 100,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Text variant="body1" color={colorSet.gray500}>
                    검색된 이미지가 없습니다.
                  </Text>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Dialog>

      <CollectionSelectModal
        isVisible={isCollectionSelectModalVisible}
        onClose={handleCollectionSelectModalClose}
        imageId={selectedImageInfo?.id ?? 0}
        onCollectionUpsertModalOpen={handleCollectionUpsertModalOpen}
        createQueryStringForImageList={createQueryStringForImageList}
        onImageCollected={handleImageCollected}
      />

      <CollectionUpsertModal
        isVisible={isCollectionUpsertModalVisible}
        showGoBackButton={showGoBackButton}
        onClose={handleCollectionUpsertModalClose}
        onGoBackButtonClick={handleGoBackToCollectionSelectModal}
      />
    </>
  );
};
